import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { MetaCrewDownloadPage, unavailableDesktopRelease, type DesktopAppRelease } from "@studio-features/meta-crew/desktop-download";
import { ApiError, errorMessage } from "../lib/api";
import { safeRedirect } from "../lib/access";
import { useAuth } from "../lib/auth";

/** 고객 콘솔 도메인이면 고객 로그인 문구 (서버 CUSTOMER_HOSTS 와 같은 기준: app.* 도메인). */
const loginScope = (): "admin" | "customer" => (typeof window !== "undefined" && /^app\./i.test(window.location.hostname) ? "customer" : "admin");

/**
 * 로그인 (시안 4893fae·e47b9af, 요청서 20절): 로그인 카드 안에 Worker 내려받기 하나, "설치 방법 보기"는 다이얼로그.
 * 배포 정보(버전·크기·배포일·주소)는 로그인 없이 GET /v1/public/desktop-app 에서 받는다. 배포본이 없으면 시안의 "준비 중" 상태.
 */
export function LoginPage({ redirect }: { redirect?: string }) {
  const login = useAuth((s) => s.login);
  const navigate = useNavigate();
  const [release, setRelease] = useState<DesktopAppRelease>(unavailableDesktopRelease);
  useEffect(() => {
    let alive = true;
    fetch("/v1/public/desktop-app")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const r = j?.data?.result as DesktopAppRelease | undefined;
        if (alive && r) setRelease(r);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return (
    <MetaCrewDownloadPage
      release={release}
      preview={false}
      loginScope={loginScope()}
      onLogin={async ({ email, password }) => {
        try {
          await login(email, password);
        } catch (er) {
          // 다른 콘솔 도메인의 계정이면 서버가 그쪽 주소를 알려 준다 (관리 콘솔 ↔ 고객 콘솔). 시안 폼은 글자만 보여 주므로 주소를 문구에 넣는다.
          const url = er instanceof ApiError ? er.detail?.consoleUrl : undefined;
          const other = typeof url === "string" && /^https?:\/\//.test(url) ? ` ${url.replace(/^https?:\/\//, "")} 에서 로그인하세요.` : "";
          throw new Error(errorMessage(er) + other);
        }
        await navigate({ to: safeRedirect(redirect) ?? "/" });
      }}
    />
  );
}
